/**
 * Cart Market Swap (AYL-129)
 *
 * On a market switch (PL <-> DE), maps the current cart to the target market's
 * equivalent products by normalised SKU, then rebuilds it on the target market
 * via a Shopify cart permalink. Works in BOTH routing modes:
 *   - subfolder:  https://www.aylacare.eu/de-de/cart/{variantId}:{qty},...
 *   - subdomain:  https://de.ayla.care/cart/{variantId}:{qty},...
 * so it never depends on cross-domain cart persistence.
 *
 * SKU patterns:
 *   - Polish AD:    PL-AD-{PET}-{CATEGORY}-{PROTEIN}-{CUT}
 *   - German AD:    DE-AD-{CATEGORY}-{PROTEIN}-{CUT}-{PET}
 *   - Polish AH/AR: PL-{BRAND}-{TYPE}-{PROTEIN}-{CUT}-{PET}
 *   - German AH/AR: DE-{BRAND}-{TYPE}-{PROTEIN}-{CUT}-{PET}
 */
class CartMarketSwap {
  constructor() {
    this.productMap = null;
    this.productMapPromise = null;
  }

  // Markets with separate product catalogues. locale -> URL base for each routing mode.
  static MARKETS = {
    pl: { subfolder: '/pl-pl', host: 'pl.ayla.care' },
    de: { subfolder: '/de-de', host: 'de.ayla.care' }
  };

  static getInstance() {
    if (!window._cartMarketSwapInstance) {
      window._cartMarketSwapInstance = new CartMarketSwap();
    }
    return window._cartMarketSwapInstance;
  }

  // Pre-fetch the product map (call when the switcher UI opens) so the redirect is instant.
  static preload() {
    const instance = CartMarketSwap.getInstance();
    instance.buildProductMap();
    return instance;
  }

  getLocaleFromCountry(countryCode) {
    const map = { PL: 'pl', DE: 'de', FR: 'fr', SE: 'sv', GB: 'en', US: 'en' };
    return map[countryCode] || 'en';
  }

  // Subdomain mode = each market on its own host. Detect by hostname — NOT by
  // shopMarket.root, which is non-empty for alternate-locale paths like de.ayla.care/pl.
  isSubdomainMode() {
    const host = (window.location.hostname || '').toLowerCase();
    return Object.keys(CartMarketSwap.MARKETS).some((k) => CartMarketSwap.MARKETS[k].host === host);
  }

  // Base URL for the target market in the active routing mode.
  targetBase(locale) {
    const m = CartMarketSwap.MARKETS[locale];
    if (!m) return null;
    return this.isSubdomainMode() ? 'https://' + m.host : window.location.origin + m.subfolder;
  }

  // Normalise a SKU to a market-agnostic key so PL/DE equivalents match.
  normalizeSKU(sku) {
    if (!sku) return null;
    const parts = sku.split('-');
    if (parts.length < 5) return null;
    const market = parts[0];
    const brand = parts[1];
    const normalizeCut = (cut) => {
      if (cut === 'PIS') return 'PS';
      if (cut === 'UDT') return 'UD';
      if (cut === 'PIT') return 'PI';
      if (cut === 'MET') return 'ME';
      if (cut === 'SET') return 'SE';
      if (cut === 'WTBT') return 'WTB';
      return cut;
    };
    if (brand === 'AD') {
      if (market === 'PL') {
        const [, , pet, category, protein, cut] = parts;
        return `AD-${category}-${protein}-${normalizeCut(cut)}-${pet}`;
      } else if (market === 'DE') {
        const [, , category, protein, cut, pet] = parts;
        return `AD-${category}-${protein}-${normalizeCut(cut)}-${pet}`;
      }
    } else {
      const normalizedParts = parts.slice(1).map((p, i) => (i >= 3 ? normalizeCut(p) : p));
      return normalizedParts.join('-');
    }
    return null;
  }

  // Build a map of all products keyed by handle and normalised SKU.
  async buildProductMap() {
    if (this.productMap) return this.productMap;
    if (this.productMapPromise) return this.productMapPromise;

    this.productMapPromise = (async () => {
      try {
        const response = await fetch('/products.json?limit=250');
        const data = await response.json();
        const map = { byHandle: {}, byNormalizedKey: {}, bySKU: {} };
        const UNAVAILABLE_TAGS = ['force-unavailable', 'force_unavailable', 'niedostepny', 'unavailable'];

        for (const product of data.products) {
          const variant = product.variants[0];
          if (!variant || !variant.sku) continue;

          const sku = variant.sku;
          const handle = product.handle;
          const market = sku.startsWith('PL-') ? 'pl' : sku.startsWith('DE-') ? 'de' : null;
          if (!market) continue;

          const normalizedKey = this.normalizeSKU(sku);
          const rawTags = product.tags;
          const tags = Array.isArray(rawTags)
            ? rawTags.map((t) => String(t).trim().toLowerCase())
            : (typeof rawTags === 'string' ? rawTags.split(',').map((t) => t.trim().toLowerCase()) : []);
          const forceUnavailable = UNAVAILABLE_TAGS.some((t) => tags.includes(t));
          map.byHandle[handle] = { sku, normalizedKey, market, title: product.title, variantId: variant.id, forceUnavailable };
          map.bySKU[sku] = handle;
          if (normalizedKey) {
            if (!map.byNormalizedKey[normalizedKey]) map.byNormalizedKey[normalizedKey] = {};
            map.byNormalizedKey[normalizedKey][market] = handle;
          }
        }

        this.productMap = map;
        return map;
      } catch (error) {
        console.error('[CartMarketSwap] Error building product map:', error);
        return { byHandle: {}, byNormalizedKey: {}, bySKU: {} };
      }
    })();

    return this.productMapPromise;
  }

  async findEquivalent(handle, targetMarket) {
    const map = await this.buildProductMap();
    const info = map.byHandle[handle];
    if (!info || !info.normalizedKey) return null;
    const equivalents = map.byNormalizedKey[info.normalizedKey];
    if (!equivalents || !equivalents[targetMarket]) return null;
    return equivalents[targetMarket];
  }

  // Resolve every cart line to a target-market variant id (or flag it).
  async resolveTargetCart(newLocale) {
    const [cart] = await Promise.all([
      fetch('/cart.js').then((r) => r.json()),
      this.buildProductMap()
    ]);

    // permalinkItems: every line carried to the target (for the subdomain rebuild).
    // addItems / removeKeys: only the lines that actually CHANGE in subfolder mode —
    // already-target lines are left untouched so they aren't merged-then-removed.
    const permalinkItems = [];
    const addItems = [];
    const removeKeys = [];
    const flagged = [];
    if (!cart.items || !cart.items.length) {
      return { permalinkItems, addItems, removeKeys, flagged, empty: true };
    }

    for (const item of cart.items) {
      const info = item.handle ? this.productMap.byHandle[item.handle] : null;
      if (info && info.market === newLocale) {
        // Already a target-market product — keep it as-is (do NOT re-add or remove it).
        permalinkItems.push({ variantId: item.variant_id, qty: item.quantity });
        continue;
      }
      const eqHandle = item.handle ? await this.findEquivalent(item.handle, newLocale) : null;
      const eqInfo = eqHandle ? this.productMap.byHandle[eqHandle] : null;
      if (eqInfo && eqInfo.variantId && !eqInfo.forceUnavailable) {
        permalinkItems.push({ variantId: eqInfo.variantId, qty: item.quantity });
        addItems.push({ variantId: eqInfo.variantId, qty: item.quantity });
        removeKeys.push(item.key);
      } else {
        // No usable equivalent (missing or force-unavailable in the target market) — drop it.
        flagged.push(item.handle || item.product_title || '');
        removeKeys.push(item.key);
      }
    }
    return { permalinkItems, addItems, removeKeys, flagged, empty: false };
  }

  /**
   * Switch to `newLocale`, rebuilding the cart on the target market via a cart
   * permalink. Calls `fallback()` (a normal market switch) when there is nothing
   * to carry, the locale has no separate catalogue, or anything goes wrong.
   */
  async swapAndRedirect(newLocale, fallback) {
    const runFallback = () => { if (typeof fallback === 'function') fallback(); };
    try {
      const base = this.targetBase(newLocale);
      if (!base) { runFallback(); return; }

      const { permalinkItems, addItems, removeKeys, flagged, empty } = await this.resolveTargetCart(newLocale);

      if (this.isSubdomainMode()) {
        // Cross-host: the target subdomain has its own cart, so rebuild the whole cart
        // via a Shopify cart permalink on that host.
        if (empty) { runFallback(); return; }
        if (!permalinkItems.length) {
          // Nothing carries over; if items were dropped, still switch host + show the notice.
          if (flagged.length) { window.location.href = `${base}/?ayla_flagged=${flagged.length}`; }
          else { runFallback(); }
          return;
        }
        const permalink = permalinkItems.map((i) => `${i.variantId}:${i.qty}`).join(',');
        // storefront=true lands on the populated cart page instead of checkout.
        let url = `${base}/cart/${permalink}?storefront=true`;
        if (flagged.length) url += `&ayla_flagged=${flagged.length}`;
        window.location.href = url;
        return;
      }

      // Subfolder mode: the cart is shared across markets on this domain. If nothing
      // changes (empty cart, or every line already in the target market), just switch.
      if (empty || (!addItems.length && !removeKeys.length)) { runFallback(); return; }

      // Add the swapped equivalents FIRST — if this fails, the original cart is left
      // untouched — then remove only the swapped/dropped source lines (already-target
      // lines stay), then navigate to the target subfolder (Shopify re-prices into the
      // target market; lands on the storefront).
      if (addItems.length) {
        const addResp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: addItems.map((i) => ({ id: i.variantId, quantity: i.qty })) })
        });
        if (!addResp.ok) { runFallback(); return; }
      }
      if (removeKeys.length) {
        const updates = {};
        removeKeys.forEach((k) => { updates[k] = 0; });
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        }).catch(() => {});
      }
      let dest = base;
      if (flagged.length) dest += '?ayla_flagged=' + flagged.length;
      window.location.href = dest;
    } catch (error) {
      console.error('[CartMarketSwap] swapAndRedirect failed:', error);
      runFallback();
    }
  }
}

window.CartMarketSwap = CartMarketSwap;

// Brief notice when a previous market switch dropped products with no equivalent in
// the new market (passed as ?ayla_flagged=N on the destination URL by swapAndRedirect).
(function () {
  try {
    var params = new URLSearchParams(window.location.search);
    var n = parseInt(params.get('ayla_flagged') || '0', 10);
    if (!n) return;
    var locale = (window.shopMarket && window.shopMarket.locale) || 'pl';
    var msgs = {
      pl: n + ' produkt(y) z Twojego koszyka nie są dostępne w tym sklepie i zostały usunięte.',
      de: n + ' Produkt(e) aus Ihrem Warenkorb sind in diesem Shop nicht verfügbar und wurden entfernt.',
      en: n + ' product(s) from your cart aren’t available in this shop and were removed.',
      fr: n + ' produit(s) de votre panier ne sont pas disponibles dans cette boutique et ont été retirés.',
      sv: n + ' produkt(er) i din varukorg är inte tillgängliga i den här butiken och har tagits bort.'
    };
    var show = function () {
      var bar = document.createElement('div');
      bar.setAttribute('role', 'status');
      bar.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);max-width:90%;z-index:100000;background:#2D4F40;color:#fff;padding:12px 18px;border-radius:8px;box-shadow:0 6px 24px rgba(0,0,0,.25);font-size:14px;line-height:1.4;';
      bar.textContent = msgs[locale] || msgs.en;
      document.body.appendChild(bar);
      setTimeout(function () { if (bar.parentNode) bar.parentNode.removeChild(bar); }, 8000);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', show);
    } else {
      show();
    }
    // Strip the param so it doesn't re-show on refresh.
    params.delete('ayla_flagged');
    var q = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash);
  } catch (e) { /* non-fatal */ }
})();
